"use strict";
// Feature: address-geolocation, Task 7.1
//
// Address_Search — pure filter over records that carry a structured
// `Address`. Composes the two mandatory guards (owner isolation + no-owner
// = no records) from Req 9.3 / 9.4 with a case-insensitive substring match
// against the five searchable fields (Req 9.1).
//
// This module is I/O-free. Resource Lambdas call it after loading the
// owner-partitioned records from DynamoDB. Backing storage is out of scope
// here — the invariants tested are structural (owner isolation, evaluation
// against structured fields, never a formatted string as source of truth).
Object.defineProperty(exports, "__esModule", { value: true });
exports.searchAddresses = exports.matchesAddressQuery = void 0;
const SEARCHABLE_FIELDS = [
    "organisation",
    "addressLine1",
    "town",
    "county",
    "postcode",
];
/**
 * Read the structured Address off a record — `structuredAddress` first
 * (Building/Contact), then `address` (Unit).
 */
function readAddress(record) {
    return record.structuredAddress ?? record.address;
}
/**
 * Case-insensitive substring test. Returns `false` for empty needles
 * (nothing to match on).
 */
function containsCI(haystack, needle) {
    if (typeof haystack !== "string" || haystack.length === 0)
        return false;
    if (needle.length === 0)
        return false;
    return haystack.toLowerCase().includes(needle.toLowerCase());
}
/**
 * Does the record match the query? Structural rule: evaluate every clause
 * against the STRUCTURED Address fields — never a joined "formatted"
 * string as source of truth (Req 9.2).
 */
function matchesAddressQuery(record, query) {
    const addr = readAddress(record);
    if (!addr)
        return false;
    if (query.kind === "term") {
        const term = query.term.trim();
        if (term.length === 0)
            return false;
        return SEARCHABLE_FIELDS.some((f) => containsCI(addr[f], term));
    }
    // 'fields' — ALL specified predicates must hold.
    const entries = Object.entries(query.fields);
    const active = entries
        .map(([k, v]) => [k, (v ?? "").trim()])
        .filter(([, v]) => v.length > 0);
    if (active.length === 0)
        return false;
    return active.every(([field, needle]) => containsCI(addr[field], needle));
}
exports.matchesAddressQuery = matchesAddressQuery;
/**
 * Filter records by owner + query. Two invariants (both testable):
 *   - `resolvedOwnerId === undefined` → return `403` with `records: []`
 *     (Req 9.4).
 *   - Every returned record has `record.ownerId === resolvedOwnerId`
 *     (Req 9.3, P-SearchTenantIsolation).
 *
 * The caller is responsible for the DynamoDB query that produced
 * `candidates` — `candidates` typically comes from a `Query` on the
 * owner partition, so pre-filtering by ownerId here is defence-in-depth
 * against a caller mistake.
 */
function searchAddresses(candidates, query, resolvedOwnerId) {
    if (typeof resolvedOwnerId !== "string" || resolvedOwnerId.length === 0) {
        return { status: 403, error: "unresolved-owner", records: [] };
    }
    const ownerScoped = candidates.filter((c) => c.ownerId === resolvedOwnerId);
    const matched = ownerScoped.filter((c) => matchesAddressQuery(c, query));
    return { status: 200, records: matched };
}
exports.searchAddresses = searchAddresses;
